import React, { useEffect, useState } from "react";
import { Container, Box, Select, Flex, Pagination, Center } from "@mantine/core";
import SelectableLicenseList from "./new_order/SelectableLicenseList";
import { getAllDistributors, getLicenses } from "@utils/api-communication/RestApiCli";
import LicenseAssignmentRequestsButton from "./LicenseAssignmentRequestsButton";
import { LicenseTypeValues, LicenceStateValues } from "../../../types/accountManagementTypes";
import { useSelector } from "react-redux";
import {
  selectUserDistributorId,
  selectUserRole,
} from "@utils/redux-utils/selectors/userSelectors";

type LicensesListProps = {
  orderId?: string;
};

const LICENSE_STATE_OPTIONS = [
  { value: "", label: "All" },
  ...LicenceStateValues.map((state) => ({
    value: state,
    label: state
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" "),
  })),
];

const LICENSE_TYPE_OPTIONS = [
  { value: "", label: "All" },
  ...LicenseTypeValues.map((type) => ({
    value: type,
    label: type
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" "),
  })),
];

export default function LicensesList(props: LicensesListProps) {
  const role = useSelector(selectUserRole);
  const userDistId = useSelector(selectUserDistributorId);
  const isAdmin = role === "admin";
  const [licenses, setLicenses] = useState<any[]>([]);
  const [orderId, setOrderId] = useState<string | null>(props.orderId || null);
  const [distId, setDistId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [licenseState, setLicenseState] = useState<any>(null);
  const [licenseType, setLicenseType] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [distributorOptions, setDistributorOptions] = useState<{ value: string; label: string }[]>(
    [],
  );

  const loadLicenses = () => {
    if (!initialized) return;
    const queryParams: any = {};
    if (distId) {
      queryParams.distId = distId;
    }
    if (orderId) {
      queryParams.orderId = orderId;
    }
    if (licenseState) {
      queryParams.licenseState = licenseState;
    }
    if (licenseType) {
      queryParams.licenseType = licenseType;
    }
    if (page) {
      queryParams.page = page;
    }
    getLicenses(
      queryParams,
      (resp: any) => {
        setLicenses(resp?.data?.licenses ?? resp?.data ?? []);
        setTotalPages(resp?.data?.totalPages ?? 1);
      },
      (err: any) => {
        console.error(err?.message || "Failed to load licenses");
      },
    );
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const orderIdFromParams = searchParams.get("orderId");
    const distIdFromParams = searchParams.get("distId");
    if (distIdFromParams) {
      setDistId(distIdFromParams);
    } else if (!isAdmin && userDistId) {
      setDistId(userDistId);
    }
    if (orderIdFromParams) {
      setOrderId(orderIdFromParams);
    }
    setInitialized(true);
  }, [isAdmin, userDistId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [distId, orderId, licenseState, licenseType]);

  useEffect(() => {
    if (!isAdmin) return;
    getAllDistributors(
      (resp: any) => {
        const list: { distId: string; country: string[] }[] = resp?.data ?? resp ?? [];
        setDistributorOptions(
          list
            .filter((d) => d.distId)
            .map((d) => ({
              value: d.distId,
              label: d.country?.length ? `${d.distId} (${d.country.join(", ")})` : d.distId,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      },
      (err: any) => console.error("Failed to load distributors", err),
    );
  }, [isAdmin]);

  useEffect(() => {
    loadLicenses();
  }, [initialized, distId, orderId, licenseState, licenseType, page]);

  return (
    <Container fluid>
      <Box p="md">
        <Flex justify={"space-between"} gap={12} align="end" mb="md">
          <Flex gap={12} wrap="wrap" align="end">
            <Select
              label="License state"
              placeholder="All states"
              data={LICENSE_STATE_OPTIONS}
              value={licenseState}
              onChange={setLicenseState}
              clearable
              style={{ minWidth: 180 }}
            />
            <Select
              label="License type"
              placeholder="All license types"
              data={LICENSE_TYPE_OPTIONS}
              value={licenseType}
              onChange={setLicenseType}
              clearable
              searchable
              style={{ minWidth: 260 }}
            />
            <Select
              label="Distributor"
              placeholder={isAdmin ? "All distributors" : "Distributor"}
              data={isAdmin ? distributorOptions : []}
              value={distId}
              onChange={isAdmin ? setDistId : undefined}
              clearable={isAdmin}
              searchable={isAdmin}
              disabled={!isAdmin}
              style={{ minWidth: 220 }}
            />
          </Flex>

          <LicenseAssignmentRequestsButton />
        </Flex>
        <SelectableLicenseList
          licenses={licenses}
          order={null}
          distId={distId}
          onDeleted={loadLicenses}
        />
        {totalPages > 1 && (
          <Center mt="md">
            <Pagination total={totalPages} value={page} onChange={setPage} size="md" />
          </Center>
        )}
      </Box>
    </Container>
  );
}
